import React from 'react';

/**
 * Column shape shared by Table, ScrollableTable and MobileCardList so one
 * array can drive the desktop table and the mobile cards.
 */
export interface TableColumn {
  key: string;
  label: string;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row: any) => React.ReactNode;
}

interface MobileCardListProps {
  columns: TableColumn[];
  data: any[];
  onRowClick?: (row: any) => void;
  emptyMessage?: string | React.ReactNode;
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  className?: string;
}

/**
 * Card representation of a table for screens below the `lg` breakpoint.
 * Renders every column as a label/value row so no table data is lost.
 * Desktop (lg+) keeps the existing table; wrap it in `hidden lg:block`
 * and place this component next to it with the same columns/data.
 */
export default function MobileCardList({
  columns,
  data,
  onRowClick,
  emptyMessage = 'No data available',
  isLoading = false,
  onLoadMore,
  hasMore = false,
  className = '',
}: MobileCardListProps) {
  const renderValue = (column: TableColumn, row: any) => {
    if (column.render) return column.render(row[column.key], row);
    const value = row[column.key];
    return value === null || value === undefined || value === '' ? '-' : value;
  };

  if (isLoading && data.length === 0) {
    return (
      <div className={`lg:hidden flex items-center justify-center py-10 ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto mb-3"></div>
          <p className="text-xs text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <div className={`lg:hidden py-10 text-center text-xs text-gray-500 ${className}`}>
        {emptyMessage}
      </div>
    );
  }

  const [headerColumn, ...restColumns] = columns;
  // Action buttons get a full-width footer row so they never wrap misaligned
  const detailColumns = restColumns.filter((column) => column.key !== 'actions');
  const actionColumn = restColumns.find((column) => column.key === 'actions');

  return (
    <div className={`lg:hidden space-y-2.5 ${className}`}>
      {data.map((row, rowIndex) => (
        <div
          key={row.id ?? rowIndex}
          onClick={() => onRowClick && onRowClick(row)}
          className={`bg-white rounded-2xl border border-ink-200 shadow-soft p-3.5 transition-transform duration-150 [&_.text-sm]:text-xs [&_.text-base]:text-[13px] [&_.text-lg]:text-sm ${
            onRowClick ? 'cursor-pointer active:bg-brand-50 active:scale-[0.98]' : ''
          }`}
        >
          {/* First column doubles as the card header */}
          <div className="pb-2 mb-2 border-b border-gray-100">
            <span className="block text-[10px] font-medium text-gray-400 uppercase tracking-wider">
              {headerColumn.label}
            </span>
            <div className="text-[13px] font-semibold text-gray-900 mt-0.5 leading-snug">
              {renderValue(headerColumn, row)}
            </div>
          </div>

          {/* Remaining columns as label/value rows */}
          <div className="space-y-1">
            {detailColumns.map((column) => (
              <div key={column.key} className="flex items-start justify-between gap-2">
                <span className="text-[11px] text-gray-500 shrink-0 pt-px">
                  {column.label}
                </span>
                <div className="text-xs font-medium text-gray-900 text-right min-w-0 break-words leading-snug">
                  {renderValue(column, row)}
                </div>
              </div>
            ))}
          </div>

          {/* Actions footer - full card width, buttons stay on one aligned row */}
          {actionColumn && (
            <div className="pt-2 mt-2 border-t border-gray-100">
              {renderValue(actionColumn, row)}
            </div>
          )}
        </div>
      ))}

      {/* Load more - button instead of infinite scroll on mobile */}
      {hasMore && onLoadMore && (
        <button
          onClick={onLoadMore}
          disabled={isLoading}
          className="w-full py-2.5 min-h-[42px] rounded-xl border border-gray-200 bg-white text-[13px] font-medium text-brand-600 hover:bg-brand-50 disabled:opacity-50"
        >
          {isLoading ? 'Loading...' : 'Load More'}
        </button>
      )}
    </div>
  );
}
